import { ReactNode } from 'react';
import { Alert, Button, Flex, Combobox, useCombobox } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import {
  actionListDialogFormValueInit,
  ActionSetting,
  ActionSettingFormValue,
  ActionType,
  alertDialogFormValueInit,
  fullScreenDialogFormValueInit,
  messageBoxFormValueInit,
  openLinkFormValueInit,
  ValidActionSettingFormValue,
} from '../../model/index.ts';
import { useMessageEditorFormContext } from '../context.ts';
import { useActionSettingDialog, ActionSettingDialog } from './ActionSetting';

import { ActionVariants } from './ActionSetting/variants';

const AVAIL_ACTION_TYPES: ActionType[] = [
  'action_list_dialog',
  'alert_dialog',
  'full_screen_dialog',
  'message_box',
  'open_link',
];

export function ActionSettingDisplay({
  actionSettingType,
  children,
}: {
  actionSettingType: ActionSetting['type'];
  children?: ReactNode;
}) {
  const currentActionTypeName = ActionVariants[actionSettingType].name;

  return (
    <>
      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle size={16} />}
        title={`點擊行為：${currentActionTypeName || '未設定'}`}
      >
        {children}
      </Alert>
    </>
  );
}

export function ActionEditor() {
  const form = useMessageEditorFormContext();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { props: actionSettingDialogProps, edit } = useActionSettingDialog();

  const actionSetting = form.getValues().actionSetting;

  const currentActionType = form.getValues().actionSetting.type;

  const options = AVAIL_ACTION_TYPES.filter((type) => {
    return type !== currentActionType;
  }).map((type) => {
    const variant = ActionVariants[type];

    return (
      <Combobox.Option value={variant.key} key={variant.key}>
        {variant.name}
      </Combobox.Option>
    );
  });

  const editAction = async (initSetting = actionSetting) => {
    const value = await edit(initSetting);

    if (!value) return;

    form.setFieldValue(
      'actionSetting',
      value as unknown as ValidActionSettingFormValue,
    );
  };

  const handleSetActionType = async (actionType: string) => {
    let initValue: ActionSettingFormValue;

    switch (actionType) {
      case 'alert_dialog':
        initValue = alertDialogFormValueInit;
        break;
      case 'action_list_dialog':
        initValue = actionListDialogFormValueInit;
        break;
      case 'open_link':
        initValue = openLinkFormValueInit;
        break;
      case 'message_box':
        initValue = messageBoxFormValueInit;
        break;
      case 'full_screen_dialog':
        initValue = fullScreenDialogFormValueInit;
        break;

      default:
        return;
    }

    const value = await edit(initValue);

    if (!value) return;

    form.setFieldValue(
      'actionSetting',
      value as unknown as ValidActionSettingFormValue,
    );
  };

  return (
    <>
      <ActionSettingDisplay actionSettingType={currentActionType}>
        <Flex justify="end" align="center" gap={10}>
          <Combobox
            store={combobox}
            width={250}
            position="bottom-start"
            withArrow
            onOptionSubmit={async (value) => {
              combobox.closeDropdown();
              handleSetActionType(value);
            }}
          >
            <Combobox.Target>
              <Button
                variant="transparent"
                color="red"
                onClick={() => combobox.toggleDropdown()}
              >
                行為類型設定
              </Button>
            </Combobox.Target>

            <Combobox.Dropdown>
              <Combobox.Options>{options}</Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
          <Button variant="outline" color="blue" onClick={() => editAction()}>
            樣式細節設定
          </Button>
        </Flex>
      </ActionSettingDisplay>
      <ActionSettingDialog {...actionSettingDialogProps} />
    </>
  );
}
